import React, { useState, useEffect } from "react";
import { Outlet, useParams, Navigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { trodoAnalyticsTheme as analyticsTheme } from "features/analytics/studio/theme";
import Sidebar from "shared/components/layout/Sidebar";
import GlobalTopBar from "shared/components/layout/GlobalTopBar";
import { BreadcrumbProvider } from "shared/context/BreadcrumbContext";
import { AnalyticsProvider } from "features/analytics/studio/context/AnalyticsContext";
import { PinnedBoardsProvider } from "features/analytics/studio/context/PinnedBoardsContext";
import InstallPanel from "features/onboarding/components/InstallPanel";
import useTeamActivation from "shared/hooks/useTeamActivation";

function AnalyticsLayoutInner() {
  const { team } = useParams();
  const { activated, pending: activationPending } = useTeamActivation();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(typeof window === 'undefined' || window.innerWidth >= 768);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      const wasMobile = isMobile;
      const nowMobile = w < 768;
      setIsMobile(nowMobile);
      if (wasMobile !== nowMobile) setIsSidebarOpen(!nowMobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobile]);

  if (!team) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <PinnedBoardsProvider teamId={team}>
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: analyticsTheme.bgBase }}>
      <div className="relative z-50 shrink-0 h-screen">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onNavigate={() => isMobile && setIsSidebarOpen(false)}
          hideMarketing={() => {}}
          isCompact={false}
          currentPage="analytics"
        />
      </div>
      {/* Right column: mirrors Dashboard's chrome so analytics pages get the
          same grey strip + rounded white panel + GlobalTopBar (breadcrumb)
          treatment as Sessions / Events / Use Cases / etc. */}
      <div className="flex flex-1 min-w-0 flex-col overflow-hidden bg-[color:var(--surface-sunken)] pt-2">
        {isMobile && !isSidebarOpen && (
          <button
            type="button"
            onClick={() => setIsSidebarOpen(true)}
            aria-label="Open navigation menu"
            className="fixed top-2.5 left-2.5 z-[60] rounded-[12px] border border-[color:var(--border-default)] bg-surface p-2 shadow-md md:hidden"
          >
            <Menu size={20} color="var(--text-primary)" />
          </button>
        )}
        {isMobile && isSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-[color:var(--surface-overlay)]"
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden
          />
        )}
        {/* White panel: relative anchor for drawers, rounded-tl cutout that
            connects the sidebar grey strip with the top edge. */}
        <div className="relative flex flex-1 min-h-0 min-w-0 flex-col overflow-hidden bg-surface rounded-tl-2xl border-t border-l border-[color:var(--border-default)]">
          {/* Single scroll container: path bar scrolls with the page */}
          <div className="flex flex-1 min-h-0 min-w-0 flex-col overflow-y-auto">
            <GlobalTopBar
              onMobileMenuToggle={isMobile ? () => setIsSidebarOpen((v) => !v) : undefined}
              isMobileMenuOpen={isSidebarOpen}
            />
            {/* Analytics is a data surface: with no traces there is nothing to
                report on, so it shows the install flow inside this same shell
                rather than an empty studio. See Dashboard for the same gate. */}
            {activationPending ? null : activated ? <Outlet /> : (
              <div className="p-3 sm:p-4 md:p-6 lg:p-8">
                <InstallPanel />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </PinnedBoardsProvider>
  );
}

export default function AnalyticsLayout() {
  return (
    <BreadcrumbProvider>
      <AnalyticsProvider>
        <AnalyticsLayoutInner />
      </AnalyticsProvider>
    </BreadcrumbProvider>
  );
}
